import React from 'react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Server } from 'lucide-react';
import { IntegrationConnectionUI } from './IntegrationConnectionUI';

export const RemoteStorageConfig = ({ node, onChangeData }: any) => {
    const config = node.data || {};
    const protocol = config.protocol || 'Local';
    
    const updateData = (key: string, value: any) => {
        onChangeData(key, value);
    };

    const handleProtocolChange = (val: string) => {
        updateData('protocol', val);
        // Reset fields
        updateData('connectionId', '');
    };

    return (
        <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm tracking-wider mb-2 flex items-center gap-2 border-b border-gray-100 dark:border-slate-800 pb-2">
                <Server className="h-4 w-4 text-indigo-500" />
                Remote File Source
            </h3>

            {/* Protocol */}
            <div className="space-y-1">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Source Type</label>
                <Select value={protocol} onValueChange={handleProtocolChange}>
                    <SelectTrigger className="h-8 text-xs bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-800 z-50">
                        <SelectItem value="Local">Local File / Upload</SelectItem>
                        <SelectItem value="S3">AWS S3 / MinIO</SelectItem>
                        <SelectItem value="AzureBlob">Azure Blob Storage</SelectItem>
                        <SelectItem value="SharePoint">Microsoft SharePoint</SelectItem>
                        <SelectItem value="OneDrive">Microsoft OneDrive</SelectItem>
                        <SelectItem value="SFTP">SFTP Server</SelectItem>
                    </SelectContent>
                </Select>
            </div>


            {/* Bucket / Namespace Area */}
            {protocol === 'S3' && (
                <div className="space-y-1 pt-1">
                    <label className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">Bucket Name (Optional)</label>
                    <Input value={config.bucketName || ''} onChange={(e) => updateData('bucketName', e.target.value)} className="h-8 text-xs bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700" placeholder="my-bucket-name" />
                </div>
            )}
            {protocol === 'AzureBlob' && (
                <div className="space-y-1 pt-1">
                    <label className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">Container Name</label>
                    <Input value={config.bucketName || ''} onChange={(e) => updateData('bucketName', e.target.value)} className="h-8 text-xs bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700" placeholder="my-container" />
                </div>
            )}
            {protocol === 'SharePoint' && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="space-y-1">
                        <label className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">Domain Name</label>
                        <Input value={config.sharepointDomain || ''} onChange={(e) => updateData('sharepointDomain', e.target.value)} className="h-8 text-xs bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700" placeholder="contoso" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">Site Name</label>
                        <Input value={config.siteName || ''} onChange={(e) => updateData('siteName', e.target.value)} className="h-8 text-xs bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700" placeholder="Finance" />
                    </div>
                </div>
            )}

            {/* Path Selection */}
            <div className="space-y-1 pt-2">
                <label className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">File Path / Object Key</label>
                <div className="flex gap-2">
                    <Input 
                        value={config.path || ''} 
                        onChange={(e) => updateData('path', e.target.value)} 
                        className="h-8 text-xs font-mono bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700 shadow-sm" 
                        placeholder={
                            protocol === 'S3' ? "folder/data.csv" :
                            protocol === 'AzureBlob' ? "folder/data.csv" :
                            protocol === 'SharePoint' ? "/Shared Documents/data.csv" :
                            protocol === 'SFTP' ? "/home/user/data.csv" :
                            "/absolute/path/or/upload.csv"
                        } 
                    />
                </div>
                <p className="text-[10px] text-gray-400 mt-1 pl-1">
                    {protocol === 'S3' && "Path relative to bucket, e.g. data/reports/2024.csv"}
                    {protocol === 'AzureBlob' && "Path relative to container, e.g. data/reports/2024.csv"}
                    {protocol === 'SharePoint' && "Path relative to site root, e.g. /Shared Documents/Reports.csv"}
                </p>
            </div>
        </div>
    );
};
